import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, FileText, DollarSign, Trash2, Pencil, Send, Eye,
  CheckCircle2, XCircle, Clock, ChevronDown, ChevronUp, Sparkles, BarChart3,
  ArrowUpRight, Download, Receipt, Building2, Phone, Mail
} from 'lucide-react';
import axios from 'axios';
import ProductLookupDropdown from '../components/products/ProductLookupDropdown';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import ProposalCreatorPage from './ProposalCreatorPage';
import ProposalAnalyticsPage from './ProposalAnalyticsPage';
import { formatCurrency as fmtCurrencyGlobal } from '../utils/locale';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-600', icon: FileText },
  { value: 'sent', label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  { value: 'viewed', label: 'Viewed', color: 'bg-purple-100 text-purple-700', icon: Eye },
  { value: 'accepted', label: 'Accepted', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  { value: 'expired', label: 'Expired', color: 'bg-amber-100 text-amber-700', icon: Clock },
];

function formatCurrency(val) {
  return fmtCurrencyGlobal(val || 0);
}

// ── Preview Modal ──────────────────────────────────────────────────
function PreviewModal({ open, onClose, proposal }) {
  const [letterhead, setLetterhead] = useState(null);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/sales/letterheads/active`).then(r => setLetterhead(r.data)).catch(() => {});
    }
  }, [open]);

  if (!proposal) return null;
  const doc_type = (proposal.doc_type || 'proposal').replace(/^\w/, c => c.toUpperCase());
  const lh = letterhead || {};
  const brandColor = lh.primary_color || '#10B981';
  const companyName = lh.company_name || 'Konnect';

  const handleDownloadPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const res = await axios.get(`${API}/api/sales/proposals/${proposal.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${doc_type}_${proposal.title?.replace(/\s+/g, '_') || 'document'}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('PDF generation failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="proposal-preview">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-blue-500" /> {doc_type} Preview</DialogTitle>
        </DialogHeader>

        {/* Document Preview */}
        <div className="border border-gray-200 rounded-lg bg-white p-6 space-y-5 shadow-sm">
          {/* Letterhead Header */}
          <div className="pb-4" style={{ borderBottom: `3px solid ${brandColor}` }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {lh.logo_url ? (
                  <img src={lh.logo_url} alt={companyName} className="h-10 object-contain" />
                ) : null}
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: brandColor }}>{companyName}</h2>
                  {lh.tagline && <p className="text-[10px] text-gray-400">{lh.tagline}</p>}
                </div>
              </div>
              <div className="text-right text-[10px] text-gray-400 space-y-0.5">
                {lh.phone && <p>{lh.phone}</p>}
                {lh.email && <p>{lh.email}</p>}
                {lh.website && <p>{lh.website}</p>}
              </div>
            </div>
            {lh.address && <p className="text-[10px] text-gray-400 mt-1">{lh.address}</p>}
            <p className="text-xs text-gray-400 mt-1">{doc_type} Document</p>
          </div>

          {/* Title & Meta */}
          <div>
            <h3 className="text-xl font-bold text-gray-900">{proposal.title}</h3>
            <div className="flex gap-3 mt-1 text-xs text-gray-500">
              <Badge className={cn("text-[10px]", STATUSES.find(s => s.value === proposal.status)?.color)}>{proposal.status}</Badge>
              <span>Valid: {proposal.valid_days || 30} days</span>
              <span>Date: {String(proposal.created_at || '').slice(0, 10)}</span>
            </div>
          </div>

          {/* Client Info */}
          <div className="bg-gray-50 rounded-lg p-4 border">
            <p className="text-xs font-semibold text-gray-500 mb-2">BILL TO</p>
            <div className="space-y-1 text-sm">
              {proposal.client_name && <p className="font-semibold">{proposal.client_name}</p>}
              {proposal.client_company && <p className="text-gray-600 flex items-center gap-1"><Building2 className="h-3 w-3" />{proposal.client_company}</p>}
              {proposal.client_email && <p className="text-gray-600 flex items-center gap-1"><Mail className="h-3 w-3" />{proposal.client_email}</p>}
              {proposal.client_phone && <p className="text-gray-600 flex items-center gap-1"><Phone className="h-3 w-3" />{proposal.client_phone}</p>}
            </div>
          </div>

          {/* Cover Letter */}
          {proposal.cover_letter && (
            <div className="bg-blue-50/50 rounded-lg p-4 border border-blue-100">
              <p className="text-xs font-semibold text-blue-600 mb-2">COVER LETTER</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{proposal.cover_letter}</p>
            </div>
          )}

          {/* Proposal Sections */}
          {(proposal.sections || []).length > 0 && proposal.sections.map((sec, i) => (
            <div key={i} className="border-l-4 border-emerald-400 pl-4">
              <h4 className="font-bold text-gray-800 text-sm mb-1">{sec.title}</h4>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{sec.content}</p>
              {sec.type === 'media' && (sec.media_urls || []).length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {sec.media_urls.map((url, j) => (
                    <img key={j} src={url} alt={sec.title} className="h-24 rounded-lg border object-cover" />
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Line Items Table */}
          {(proposal.line_items || []).length > 0 && (
            <div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200 text-left">
                    <th className="py-2 font-semibold text-gray-700">Item</th>
                    <th className="py-2 text-center font-semibold text-gray-700">Qty</th>
                    <th className="py-2 text-right font-semibold text-gray-700">Price</th>
                    <th className="py-2 text-right font-semibold text-gray-700">Disc</th>
                    <th className="py-2 text-right font-semibold text-gray-700">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {proposal.line_items.map((item, i) => {
                    const total = (item.quantity || 1) * (item.unit_price || 0) * (1 - (item.discount_pct || 0) / 100);
                    return (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-2">{item.name}</td>
                        <td className="py-2 text-center">{item.quantity}</td>
                        <td className="py-2 text-right">{formatCurrency(item.unit_price)}</td>
                        <td className="py-2 text-right">{item.discount_pct > 0 ? `${item.discount_pct}%` : '—'}</td>
                        <td className="py-2 text-right font-medium">{formatCurrency(total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex justify-end mt-3 pt-2 border-t-2 border-gray-200">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Total Amount</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(proposal.subtotal)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {proposal.notes && (
            <div className="bg-gray-50 rounded-lg p-3 border">
              <p className="text-xs font-semibold text-gray-500 mb-1">NOTES</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{proposal.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="text-center pt-4 border-t text-[10px] text-gray-400">
            {lh.footer_text || `Generated by ${companyName}`}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleDownloadPDF} className="bg-red-600 hover:bg-red-700 gap-1.5" data-testid="download-pdf-btn">
            <Download className="h-3.5 w-3.5" /> Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Create/Edit Modal ────────────────────────────────────────────────
function ProposalModal({ open, onClose, onSaved, editing, products, defaultDocType = 'proposal' }) {
  const [form, setForm] = useState({ title: '', doc_type: defaultDocType, client_name: '', client_email: '', client_phone: '', client_company: '', client_address: '', notes: '', valid_days: 30, cover_letter: '', sections: [], media_attachments: [] });
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [sourceType, setSourceType] = useState('lead');
  const [templates, setTemplates] = useState([]);
  const [showSections, setShowSections] = useState(false);
  const [vaultDocs, setVaultDocs] = useState([]);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/leads`).then(r => setContacts(r.data?.leads || r.data || [])).catch(() => {});
      axios.get(`${API}/api/clients/dropdown`).then(r => setClients(r.data || [])).catch(() => {});
      axios.get(`${API}/api/sales/proposal-templates`).then(r => setTemplates([...(r.data?.prebuilt || []), ...(r.data?.custom || [])])).catch(() => {});
      axios.get(`${API}/api/operations/documents`).then(r => setVaultDocs(r.data || [])).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (editing) {
      setForm({ title: editing.title || '', doc_type: editing.doc_type || defaultDocType, client_name: editing.client_name || '', client_email: editing.client_email || '', client_phone: editing.client_phone || '', client_company: editing.client_company || '', client_address: editing.client_address || '', notes: editing.notes || '', valid_days: editing.valid_days || 30, cover_letter: editing.cover_letter || '', sections: editing.sections || [], media_attachments: editing.media_attachments || [] });
      setItems(editing.line_items || []);
      setShowSections((editing.sections || []).length > 0 || !!(editing.cover_letter));
    } else {
      setForm({ title: '', doc_type: defaultDocType, client_name: '', client_email: '', client_phone: '', client_company: '', client_address: '', notes: '', valid_days: 30, cover_letter: '', sections: [], media_attachments: [] });
      setItems([]);
      setShowSections(false);
    }
  }, [editing, open, defaultDocType]);

  const addItem = () => setItems(prev => [...prev, { name: '', description: '', quantity: 1, unit_price: 0, discount_pct: 0 }]);
  const updateItem = (idx, key, value) => setItems(prev => prev.map((item, i) => i === idx ? { ...item, [key]: value } : item));
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const addFromCatalog = (product) => setItems(prev => [...prev, { product_id: product.id, name: product.name, description: product.description || '', quantity: 1, unit_price: product.price, discount_pct: 0 }]);

  const loadTemplate = (templateId) => {
    const t = templates.find(x => x.id === templateId);
    if (!t) return;
    setForm(p => ({
      ...p,
      cover_letter: (t.cover_letter || '').replace('{client_name}', p.client_name || '{client_name}').replace('{company_name}', 'Your Company').replace('{valid_days}', String(p.valid_days)),
      sections: (t.sections || []).map(s => ({ ...s, content: s.content || '' })),
    }));
    setShowSections(true);
    toast.success(`Template "${t.name}" loaded`);
  };

  const addSection = () => setForm(p => ({ ...p, sections: [...p.sections, { title: '', content: '', type: 'text', media_urls: [] }] }));
  const updateSection = (idx, key, val) => setForm(p => ({ ...p, sections: p.sections.map((s, i) => i === idx ? { ...s, [key]: val } : s) }));
  const removeSection = (idx) => setForm(p => ({ ...p, sections: p.sections.filter((_, i) => i !== idx) }));

  const subtotal = items.reduce((sum, item) => {
    const lineTotal = (item.quantity || 1) * (item.unit_price || 0);
    return sum + lineTotal - lineTotal * ((item.discount_pct || 0) / 100);
  }, 0);

  const handleContactSelect = (contactId) => {
    if (sourceType === 'lead') {
      const c = contacts.find(c => (c.id || c._id || c.lead_id) === contactId);
      if (c) setForm(p => ({ ...p, client_name: c.full_name || c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim(), client_email: c.email || '', client_phone: c.phone || '', client_company: c.company || c.business_name || '', client_address: c.address || '' }));
    } else {
      const c = clients.find(c => c.id === contactId);
      if (c) setForm(p => ({ ...p, client_name: c.contact_name || '', client_email: c.email || '', client_phone: c.phone || '', client_company: c.company_name || '', client_address: [c.address, c.city, c.country].filter(Boolean).join(', ') }));
    }
  };

  const sourceList = sourceType === 'lead'
    ? contacts.filter(c => c.id || c._id || c.lead_id).map(c => ({ id: String(c.id || c._id || c.lead_id), name: c.full_name || c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email, sub: `${c.company || ''} ${c.email || ''}`.trim() }))
    : clients.map(c => ({ id: c.id, name: c.contact_name, sub: `${c.company_name || ''} ${c.email || ''}`.trim() }));

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (items.length === 0) { toast.error('Add at least one line item'); return; }
    setSaving(true);
    try {
      const payload = { ...form, line_items: items, cover_letter: form.cover_letter, sections: form.sections, media_attachments: form.media_attachments };
      if (editing) {
        const { data } = await axios.put(`${API}/api/sales/proposals/${editing.id}`, payload);
        toast.success(`${form.doc_type === 'quote' ? 'Quote' : 'Proposal'} updated`);
        onSaved(data, true);
      } else {
        const { data } = await axios.post(`${API}/api/sales/proposals`, payload);
        toast.success(`${form.doc_type === 'quote' ? 'Quote' : 'Proposal'} created`);
        onSaved(data, false);
      }
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const docLabel = form.doc_type === 'quote' ? 'Quote' : 'Proposal';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-blue-500" />{editing ? 'Edit' : 'Create'} {docLabel}</DialogTitle>
          <DialogDescription>Build a {docLabel.toLowerCase()} with line items from your catalog</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder={`${docLabel} title...`} data-testid="proposal-title-input" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.doc_type} onValueChange={v => setForm(p => ({...p, doc_type: v}))}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent className="z-[10000]">
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="quote">Quote</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valid (days)</Label>
              <Input type="number" min="1" value={form.valid_days} onChange={e => setForm(p => ({...p, valid_days: parseInt(e.target.value) || 30}))} />
            </div>
          </div>

          {/* Lead/Client Selector */}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs font-semibold">Pull from:</Label>
              <Button type="button" size="sm" variant={sourceType === 'lead' ? 'default' : 'outline'} className={`h-6 text-[10px] ${sourceType === 'lead' ? 'bg-blue-600' : ''}`} onClick={() => setSourceType('lead')}>Leads</Button>
              <Button type="button" size="sm" variant={sourceType === 'client' ? 'default' : 'outline'} className={`h-6 text-[10px] ${sourceType === 'client' ? 'bg-emerald-600' : ''}`} onClick={() => setSourceType('client')}>Clients</Button>
            </div>
            {sourceList.length > 0 && (
              <Select onValueChange={handleContactSelect}>
                <SelectTrigger className="h-8 text-sm" data-testid="proposal-contact-select"><SelectValue placeholder={`Select a ${sourceType}...`} /></SelectTrigger>
                <SelectContent className="z-[10000]">{sourceList.map(c => <SelectItem key={c.id} value={c.id}>{c.name} {c.sub ? `— ${c.sub}` : ''}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Client Name</Label><Input value={form.client_name} onChange={e => setForm(p => ({...p, client_name: e.target.value}))} className="bg-white" data-testid="proposal-client-name" /></div>
            <div><Label>Client Email</Label><Input value={form.client_email} onChange={e => setForm(p => ({...p, client_email: e.target.value}))} className="bg-white" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Company</Label><Input value={form.client_company} onChange={e => setForm(p => ({...p, client_company: e.target.value}))} className="bg-white" /></div>
            <div><Label>Phone</Label><Input value={form.client_phone} onChange={e => setForm(p => ({...p, client_phone: e.target.value}))} className="bg-white" /></div>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">Line Items</Label>
              <div className="flex gap-1">
                {products.length > 0 && (
                  <Select onValueChange={v => { const p = products.find(x => x.id === v); if (p) addFromCatalog(p); }}>
                    <SelectTrigger className="h-7 text-xs w-36"><SelectValue placeholder="From catalog..." /></SelectTrigger>
                    <SelectContent className="z-[10000]">{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({formatCurrency(p.price)})</SelectItem>)}</SelectContent>
                  </Select>
                )}
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="h-7 text-xs gap-1"><Plus className="h-3 w-3" /> Custom</Button>
              </div>
            </div>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center p-2 bg-gray-50 rounded-lg border">
                  <div className="col-span-4"><Input value={item.name} onChange={e => updateItem(idx, 'name', e.target.value)} placeholder="Item name" className="text-xs h-8" /></div>
                  <div className="col-span-2"><Input type="number" min="0" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 1)} placeholder="Qty" className="text-xs h-8" /></div>
                  <div className="col-span-2"><Input type="number" min="0" step="0.01" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} placeholder="Price" className="text-xs h-8" /></div>
                  <div className="col-span-2"><Input type="number" min="0" max="100" value={item.discount_pct} onChange={e => updateItem(idx, 'discount_pct', parseFloat(e.target.value) || 0)} placeholder="Disc %" className="text-xs h-8" /></div>
                  <div className="col-span-1 text-right text-xs font-medium text-gray-700">{formatCurrency((item.quantity || 1) * (item.unit_price || 0) * (1 - (item.discount_pct || 0) / 100))}</div>
                  <div className="col-span-1 text-right"><button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button></div>
                </div>
              ))}
              {items.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No items yet — add from catalog or custom</p>}
            </div>
            {items.length > 0 && (
              <div className="flex justify-end mt-2 pr-8">
                <div className="text-right"><p className="text-xs text-gray-500">Subtotal</p><p className="text-lg font-bold text-gray-900">{formatCurrency(subtotal)}</p></div>
              </div>
            )}
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Payment terms, special conditions..." rows={2} /></div>

          {/* Template Loader */}
          {templates.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Label className="text-xs font-semibold text-blue-700">Load from Template</Label>
              <Select onValueChange={loadTemplate}>
                <SelectTrigger className="mt-1 h-8 text-xs bg-white" data-testid="template-select"><SelectValue placeholder="Choose a template..." /></SelectTrigger>
                <SelectContent className="z-[10000]">{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.category})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          {/* Cover Letter & Sections Toggle */}
          <div className="flex items-center gap-2">
            <Button type="button" variant={showSections ? 'default' : 'outline'} size="sm" className="h-7 text-xs gap-1" onClick={() => setShowSections(!showSections)}>
              <FileText className="h-3 w-3" />{showSections ? 'Hide' : 'Add'} Cover Letter & Sections
            </Button>
          </div>

          {showSections && (
            <div className="space-y-3 p-3 border border-dashed border-blue-300 rounded-lg bg-blue-50/50">
              <div><Label className="text-xs font-semibold">Cover Letter</Label>
                <Textarea value={form.cover_letter} onChange={e => setForm(p => ({...p, cover_letter: e.target.value}))} placeholder="Dear {client_name},\n\nThank you for considering our services..." rows={4} className="mt-1 text-sm bg-white" data-testid="cover-letter-input" /></div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold">Proposal Sections</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addSection} className="h-6 text-[10px] gap-1"><Plus className="h-3 w-3" />Add Section</Button>
                </div>
                {form.sections.map((sec, idx) => (
                  <div key={idx} className="p-2 bg-white rounded-lg border mb-2">
                    <div className="flex gap-2 items-center mb-1">
                      <Input value={sec.title} onChange={e => updateSection(idx, 'title', e.target.value)} placeholder="Section title" className="text-xs h-7 flex-1" />
                      <Select value={sec.type || 'text'} onValueChange={v => updateSection(idx, 'type', v)}>
                        <SelectTrigger className="h-7 text-[10px] w-24"><SelectValue /></SelectTrigger>
                        <SelectContent className="z-[10000]">
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="media">Media</SelectItem>
                          <SelectItem value="pricing">Pricing</SelectItem>
                        </SelectContent>
                      </Select>
                      <button onClick={() => removeSection(idx)} className="text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    <Textarea value={sec.content} onChange={e => updateSection(idx, 'content', e.target.value)} placeholder="Section content..." rows={2} className="text-xs" />
                    {sec.type === 'media' && (
                      <Input value={(sec.media_urls || []).join(', ')} onChange={e => updateSection(idx, 'media_urls', e.target.value.split(',').map(u => u.trim()).filter(Boolean))} placeholder="Media URLs (comma separated)" className="text-xs h-7 mt-1" />
                    )}
                  </div>
                ))}
                {form.sections.length === 0 && <p className="text-[10px] text-gray-400 text-center py-2">No sections — add one or load from template</p>}
              </div>
            </div>
          )}

          {/* Document Vault Attachments */}
          {vaultDocs.length > 0 && (
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <Label className="text-xs font-semibold text-indigo-700">Attach from Document Vault</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {vaultDocs.slice(0, 15).map(doc => {
                  const isAttached = form.media_attachments.includes(doc.file_url || doc.url || doc.id);
                  const docUrl = doc.file_url || doc.url || doc.id;
                  return (
                    <button key={doc.id} onClick={() => {
                      if (isAttached) setForm(p => ({ ...p, media_attachments: p.media_attachments.filter(u => u !== docUrl) }));
                      else setForm(p => ({ ...p, media_attachments: [...p.media_attachments, docUrl] }));
                    }}
                      className={cn("px-2.5 py-1 text-[10px] rounded-full border transition-all",
                        isAttached ? "bg-indigo-100 border-indigo-400 text-indigo-700 font-medium" : "border-gray-300 text-gray-500 hover:border-indigo-300"
                      )} data-testid={`vault-attach-${doc.id}`}>
                      {isAttached ? '✓ ' : ''}{doc.title || doc.name || 'Document'}
                    </button>
                  );
                })}
              </div>
              {form.media_attachments.length > 0 && <p className="text-[9px] text-indigo-500 mt-1">{form.media_attachments.length} document(s) attached</p>}
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-proposal-btn">{saving ? 'Saving...' : editing ? 'Update' : `Create ${docLabel}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Document List (shared by Proposals & Quotes tabs) ────────────────
function DocumentListView({ docType }) {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [propRes, prodRes] = await Promise.all([
        axios.get(`${API}/api/sales/proposals`),
        axios.get(`${API}/api/sales/products`),
      ]);
      setItems(propRes.data.filter(p => (p.doc_type || 'proposal') === docType));
      setProducts(prodRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [docType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaved = (item, isUpdate) => {
    if ((item.doc_type || 'proposal') === docType) {
      if (isUpdate) setItems(prev => prev.map(p => p.id === item.id ? item : p));
      else setItems(prev => [item, ...prev]);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete this ${docType}?`)) return;
    try {
      await axios.delete(`${API}/api/sales/proposals/${id}`);
      setItems(prev => prev.filter(p => p.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.put(`${API}/api/sales/proposals/${id}`, { status });
      setItems(prev => prev.map(p => p.id === id ? data : p));
      toast.success(`Status: ${status}`);
    } catch { toast.error('Failed'); }
  };

  const handleElevateToQuote = async (id) => {
    try {
      await axios.post(`${API}/api/sales/proposals/${id}/elevate-to-quote`);
      toast.success('Proposal elevated to Quote! Check Quotes tab.');
    } catch { toast.error('Failed to elevate'); }
  };

  const totalValue = items.reduce((s, p) => s + (p.subtotal || 0), 0);
  const acceptedValue = items.filter(p => p.status === 'accepted').reduce((s, p) => s + (p.subtotal || 0), 0);
  const label = docType === 'quote' ? 'Quote' : 'Proposal';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">{items.length} {label.toLowerCase()}s · {formatCurrency(totalValue)} total · {formatCurrency(acceptedValue)} accepted</p>
        <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 h-9 gap-1.5" data-testid={`create-${docType}-btn`}><Plus className="h-4 w-4" /> New {label}</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300">
          <CardContent className="p-10 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700 mb-1">No {label.toLowerCase()}s yet</h3>
            <p className="text-sm text-gray-500 mb-4">Create your first {label.toLowerCase()} with line items</p>
            <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-blue-600 hover:bg-blue-700 gap-1.5"><Plus className="h-4 w-4" /> New {label}</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(p => {
            const statusInfo = STATUSES.find(s => s.value === p.status) || STATUSES[0];
            const expanded = expandedId === p.id;
            return (
              <Card key={p.id} className="border-gray-200" data-testid={`${docType}-card-${p.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpandedId(expanded ? null : p.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-bold text-gray-900">{p.title}</h3>
                        <Badge className={`${statusInfo.color} text-[10px]`}>{statusInfo.label}</Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {p.client_name || 'No client'}
                        {p.client_company ? ` · ${p.client_company}` : ''}
                        {p.client_email ? ` · ${p.client_email}` : ''}
                      </p>
                    </div>
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(p.subtotal)}</p>
                    {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                  {expanded && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="space-y-1 mb-3">
                        {(p.line_items || []).map((item, i) => (
                          <div key={i} className="flex items-center gap-3 text-xs text-gray-600 py-1">
                            <span className="flex-1">{item.name}</span>
                            <span className="text-gray-400">{item.quantity} x {formatCurrency(item.unit_price)}</span>
                            {item.discount_pct > 0 && <span className="text-red-500">-{item.discount_pct}%</span>}
                            <span className="font-medium text-gray-700">{formatCurrency((item.quantity || 1) * (item.unit_price || 0) * (1 - (item.discount_pct || 0) / 100))}</span>
                          </div>
                        ))}
                      </div>
                      {p.notes && <p className="text-xs text-gray-500 mb-3 bg-gray-50 rounded p-2">{p.notes}</p>}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {/* Preview */}
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={e => { e.stopPropagation(); setPreviewItem(p); }} data-testid={`preview-${p.id}`}>
                          <Eye className="h-3 w-3" /> Preview
                        </Button>
                        {/* Edit */}
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={e => { e.stopPropagation(); setEditing(p); setModalOpen(true); }}>
                          <Pencil className="h-3 w-3" /> Edit
                        </Button>
                        {/* Status Actions */}
                        {p.status === 'draft' && <Button size="sm" className="h-7 text-xs gap-1 bg-blue-600 hover:bg-blue-700" onClick={e => { e.stopPropagation(); updateStatus(p.id, 'sent'); }}><Send className="h-3 w-3" /> Mark Sent</Button>}
                        {p.status === 'sent' && <Button size="sm" className="h-7 text-xs gap-1 bg-green-600 hover:bg-green-700" onClick={e => { e.stopPropagation(); updateStatus(p.id, 'accepted'); }}><CheckCircle2 className="h-3 w-3" /> Accept</Button>}
                        {p.status === 'sent' && <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-red-500" onClick={e => { e.stopPropagation(); updateStatus(p.id, 'rejected'); }}><XCircle className="h-3 w-3" /> Reject</Button>}
                        {/* Elevate to Quote (only for proposals) */}
                        {docType === 'proposal' && (
                          <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-purple-600 border-purple-200 hover:bg-purple-50" onClick={e => { e.stopPropagation(); handleElevateToQuote(p.id); }} data-testid={`elevate-${p.id}`}>
                            <ArrowUpRight className="h-3 w-3" /> Elevate to Quote
                          </Button>
                        )}
                        {/* Download PDF */}
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={async e => {
                          e.stopPropagation();
                          try {
                            const res = await axios.get(`${API}/api/sales/proposals/${p.id}/pdf`, { responseType: 'blob' });
                            const url = URL.createObjectURL(new Blob([res.data]));
                            const a = document.createElement('a'); a.href = url; a.download = `${label}_${p.title?.replace(/\s+/g,'_')}.pdf`; a.click();
                            toast.success('PDF downloaded');
                          } catch { toast.error('PDF failed'); }
                        }}>
                          <Download className="h-3 w-3" /> PDF
                        </Button>
                        {/* Delete */}
                        <Button variant="outline" size="sm" className="h-7 text-xs text-red-500 hover:bg-red-50 ml-auto" onClick={e => { e.stopPropagation(); handleDelete(p.id); }}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ProposalModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} products={products} defaultDocType={docType} />
      <PreviewModal open={!!previewItem} onClose={() => setPreviewItem(null)} proposal={previewItem} />
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────
export default function ProposalsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'proposals';

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="proposals-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0"><FileText className="h-5 w-5 text-blue-500" /></div>
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Proposals & Quotes<PageInfoTooltip pageKey="proposals" /></h1>
          <p className="text-xs text-gray-400">Create, manage, and track proposals and quotes</p>
        </div>
      </div>

      <Tabs defaultValue={initialTab} onValueChange={v => setSearchParams(v === 'proposals' ? {} : { tab: v }, { replace: true })}>
        <TabsList className="bg-gray-100/80">
          <TabsTrigger value="proposals" className="gap-1.5 text-xs" data-testid="tab-proposals"><FileText className="h-3.5 w-3.5" />Proposals</TabsTrigger>
          <TabsTrigger value="quotes" className="gap-1.5 text-xs" data-testid="tab-quotes"><Receipt className="h-3.5 w-3.5" />Quotes</TabsTrigger>
          <TabsTrigger value="creator" className="gap-1.5 text-xs" data-testid="tab-creator"><Sparkles className="h-3.5 w-3.5" />AI Creator</TabsTrigger>
          <TabsTrigger value="analytics" className="gap-1.5 text-xs" data-testid="tab-analytics"><BarChart3 className="h-3.5 w-3.5" />Analytics</TabsTrigger>
        </TabsList>
        <TabsContent value="proposals"><DocumentListView docType="proposal" /></TabsContent>
        <TabsContent value="quotes"><DocumentListView docType="quote" /></TabsContent>
        <TabsContent value="creator"><ProposalCreatorPage /></TabsContent>
        <TabsContent value="analytics"><ProposalAnalyticsPage /></TabsContent>
      </Tabs>
    </div>
  );
}
